/** @jsxRuntime classic */
/** @jsx jsx */
import { jsx } from '@emotion/core';
import { Modal } from 'antd';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ThemedStyleFn, useStyles } from '~src/hooks/useStyles';
import { Fragment } from 'react';

type HelpModalProps = {
  visible: boolean;
  onClose: () => void;
  headerId: string;
};

const StatusIcon = ({ icon, color }: { icon: any; color: string }) => (
  <FontAwesomeIcon icon={icon} style={{ color, marginRight: 6 }} />
);

const HelpEN = () => (
  <Fragment>
    <span className="help-head">Vendors</span>
    <span className="help-text">
      This page provides an overview of vendors participating in the tender
      process.
    </span>
    <span className="help-text">
      Additional details and actions for each vendor can be accessed by
      right-clicking on their row.
    </span>
    <span className="help-text">
      Bulk actions can be performed using the cherry picker functionality by
      selecting the checkboxes on the left side of the list.
    </span>
    <br />
    <span className="help-head">Vendor Statuses</span>
    <span className="help-text">
      Each vendor’s invitation and participation status is displayed on the left
      side of the list.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'stopwatch']} color="grey" /> Awaiting
    </span>
    <span className="help-text">
      The vendor has been added to the registration list but has not yet been
      formally notified.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'envelope']} color="orange" /> Invited
    </span>
    <span className="help-text">
      An invitation email has been sent to the vendor, inviting them to
      participate in the tender process.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'envelope-open-text']} color="orange" /> Opened
    </span>
    <span className="help-text">
      The vendor has received and accessed the invitation link.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['fad', 'check-circle']} color="green" /> Active
    </span>
    <span className="help-text">
      The vendor has accepted the invitation and is actively participating in
      the tender process.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'ban']} color="red" /> Rejected
    </span>
    <span className="help-text">
      The vendor has declined the invitation to participate in the tender.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'trash']} color="purple" /> Deleted
    </span>
    <span className="help-text">
      This vendor invitation has been deleted by a tender administrator.
    </span>

    <br />
    <span className="help-head">Contract Statuses</span>
    <span className="help-text">
      Vendors may choose to participate in each sub-contract within the tender
      individually.
    </span>
    <span className="help-text">
      If the tender includes multiple contracts, each will be represented by a
      floating dot.
    </span>
    <span className="help-text">
      These dots are consistently ordered based on their sequence on the
      contracts page.
    </span>

    <span className="help-sub-head">Awaiting Participation</span>
    <span className="help-text">
      The vendor has not yet indicated interest in this contract.
    </span>

    <span className="help-sub-head">Participating</span>
    <span className="help-text">
      The vendor has expressed interest in this contract but has not yet
      submitted their final offer.
    </span>

    <span className="help-sub-head">Delivered</span>
    <span className="help-text">
      The vendor has submitted their completed offer for this contract.
    </span>

    <span className="help-sub-head">Deleted</span>
    <span className="help-text">
      The vendor has removed their offer for this contract.
    </span>
  </Fragment>
);

const HelpDA = () => (
  <Fragment>
    <span className="help-head">Leverandører</span>
    <span className="help-text">
      Denne side giver et overblik over de leverandører, der deltager i
      udbudsprocessen.
    </span>
    <span className="help-text">
      Yderligere oplysninger og handlinger for hver leverandør kan tilgås ved at
      højreklikke på deres række.
    </span>
    <span className="help-text">
      Massehandlinger kan udføres ved hjælp af cherry picker-funktionen ved at
      markere afkrydsningsfelterne i venstre side af listen.
    </span>
    <br />
    <span className="help-head">Leverandørstatusser</span>
    <span className="help-text">
      Status for hver leverandørs invitation og deltagelse vises i venstre side
      af listen.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'stopwatch']} color="grey" /> Afventer
    </span>
    <span className="help-text">
      Leverandøren er blevet tilføjet til registreringslisten, men er endnu ikke
      blevet formelt underrettet.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'envelope']} color="orange" /> Inviteret
    </span>
    <span className="help-text">
      En invitationsmail er sendt til leverandøren med opfordring til at deltage
      i udbudsprocessen.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'envelope-open-text']} color="orange" /> Åbnet
    </span>
    <span className="help-text">
      Leverandøren har modtaget og åbnet invitationslinket.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['fad', 'check-circle']} color="green" /> Aktiv
    </span>
    <span className="help-text">
      Leverandøren har accepteret invitationen og deltager aktivt i
      udbudsprocessen.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'ban']} color="red" /> Afvist
    </span>
    <span className="help-text">
      Leverandøren har afvist invitationen om at deltage i udbuddet.
    </span>

    <span className="help-sub-head">
      <StatusIcon icon={['far', 'trash']} color="purple" /> Slettet
    </span>
    <span className="help-text">
      Denne leverandørinvitation er blevet slettet af en udbudsadministrator.
    </span>

    <br />
    <span className="help-head">Kontraktstatusser</span>
    <span className="help-text">
      Leverandører kan vælge at deltage i hver enkelt delkontrakt i udbuddet
      individuelt.
    </span>
    <span className="help-text">
      Hvis udbuddet indeholder flere kontrakter, vil hver blive repræsenteret af
      en flydende prik.
    </span>
    <span className="help-text">
      Prikkerne vises altid i samme rækkefølge som på kontraktsiden.
    </span>

    <span className="help-sub-head">Afventer deltagelse (grå)</span>
    <span className="help-text">
      Leverandøren har endnu ikke tilkendegivet interesse i denne kontrakt.
    </span>

    <span className="help-sub-head">Deltager / Ikke færdiggjort (gul)</span>
    <span className="help-text">
      Leverandøren har vist interesse i denne kontrakt, men har endnu ikke
      indsendt sit endelige tilbud.
    </span>

    <span className="help-sub-head">Indsendt (grøn)</span>
    <span className="help-text">
      Leverandøren har færdiggjort og indsendt sit tilbud for denne kontrakt.
    </span>

    <span className="help-sub-head">Slettet (rød)</span>
    <span className="help-text">
      Leverandøren har slettet sit tilbud for denne kontrakt.
    </span>
  </Fragment>
);

const getHelpComponent = (locale: string) => {
  switch (locale) {
    case 'da':
      return <HelpDA />;
    default:
      return <HelpEN />;
  }
};

export const SharesHelpInfoModal = ({
  visible,
  onClose,
  headerId,
}: HelpModalProps) => {
  const intl = useIntl();
  const styles = useStyles(styleFn);

  return (
    <Modal
      visible={visible}
      title={intl.formatMessage({ id: headerId })}
      onCancel={onClose}
      footer={null}
      width={700}>
      <div css={styles.helpContent}>{getHelpComponent(intl.locale)}</div>
    </Modal>
  );
};

export const styleFn: ThemedStyleFn = (theme) => ({
  helpContent: {
    '.help-head': {
      display: 'block',
      fontSize: 18,
      fontWeight: 'bold',
      marginTop: 18,
      marginBottom: 8,
      color: theme.colors.darkGray ?? '#333',
    },
    '.help-sub-head': {
      display: 'flex',
      alignItems: 'center',
      fontWeight: 600,
      marginTop: 14,
      color: theme.colors.black ?? '#444',
      gap: 8,
    },
    '.help-text': {
      display: 'block',
      fontSize: 14,
      margin: '4px 0 8px',
      color: theme.colors.black ?? '#666',
    },
  },
});
